import { Input } from '@/components/ui/FormControls';
import { useEffect, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { salesDateRange } from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import type { DashboardRange } from '@/hooks/useDashboardRange';
import { operationsRpc } from '@/lib/operations';
import DatePicker from './DatePicker';
export default function DashboardPeriod({
  range,
  today,
  onApply,
  onMonth,
  onReset,
}: {
  range: DashboardRange;
  today: string;
  onApply: (r: DashboardRange) => void;
  onMonth: (v: string) => void;
  onReset: () => void;
}) {
  const { organization } = useAuth();
  const filtersId = useId();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draft, setDraft] = useState(range),
    [error, setError] = useState(''),
    [loading, setLoading] = useState(false);
  useEffect(() => setDraft(range), [range.from, range.to]);
  function apply(next: DashboardRange) {
    try {
      onApply(next);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function all() {
    setLoading(true);
    setError('');
    try {
      const result = await operationsRpc<{ range: DashboardRange }>('get_dashboard_summary_v1', {
        p_filters: { all_time: true },
      });
      apply(result.range);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  return (
    <section
      aria-label="Dashboard period"
      className="grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
    >
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Selected period</h2>
          <p className="text-xs text-muted-foreground">
            {range.from} to {range.to} · {organization?.timezone || 'Asia/Kolkata'}
          </p>
        </div>
        <button
          type="button"
          aria-expanded={filtersOpen}
          aria-controls={filtersId}
          aria-label={`${filtersOpen ? 'Collapse' : 'Expand'} dashboard date filters`}
          onClick={() => setFiltersOpen((open) => !open)}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-border px-3 text-xs font-medium hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
        >
          Filters
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 transition-transform motion-reduce:transition-none ${filtersOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      <div id={filtersId} className={filtersOpen ? 'contents' : 'hidden sm:contents'}>
        <div className="flex flex-wrap gap-2">
          {(['today', '7d', '30d'] as const).map((p, i) => (
            <button
              key={p}
              className="min-h-11 rounded-lg border border-border px-3 text-xs"
              onClick={() => apply(salesDateRange(p, organization?.timezone))}
            >
              {['Today', 'Last 7 days', 'Last 30 days'][i]}
            </button>
          ))}
          <button
            className="min-h-11 rounded-lg border border-border px-3 text-xs"
            onClick={onReset}
          >
            This month
          </button>
          <button
            className="min-h-11 rounded-lg border border-border px-3 text-xs"
            onClick={() => {
              const d = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 2, 1);
              onMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
            }}
          >
            Last month
          </button>
          <button
            className="min-h-11 rounded-lg border border-border px-3 text-xs"
            disabled={loading}
            onClick={() => void all()}
          >
            {loading ? 'Loading…' : 'All time'}
          </button>
        </div>
        <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2 lg:grid-cols-4">
          <label className="space-y-1 text-xs">
            Choose month
            <Input
              aria-label="Choose dashboard month"
              type="month"
              max={today.slice(0, 7)}
              value={range.from.slice(0, 7)}
              onChange={(e) => {
                try {
                  onMonth(e.target.value);
                  setError('');
                } catch (x) {
                  setError((x as Error).message);
                }
              }}
              className="min-h-11 w-full rounded-xl border border-input bg-background px-3 text-sm"
            />
          </label>
          <label className="space-y-1 text-xs">
            From
            <DatePicker
              value={draft.from}
              max={today}
              onChange={(v) => setDraft({ ...draft, from: v || '' })}
              helpLabel="From"
            />
          </label>
          <label className="space-y-1 text-xs">
            To
            <DatePicker
              value={draft.to}
              min={draft.from}
              max={today}
              onChange={(v) => setDraft({ ...draft, to: v || '' })}
              helpLabel="To"
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              className="min-h-11 flex-1 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
              onClick={() => apply(draft)}
            >
              Apply dates
            </button>
            <button
              className="min-h-11 rounded-xl border border-border px-3 text-sm"
              onClick={() => {
                setDraft(range);
                setError('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive sm:col-span-2">
          {error}
        </p>
      )}
    </section>
  );
}
